import Link from "next/link";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

type Common = {
  variant?: Variant;
  size?: Size;
  full?: boolean;
  icon?: ReactNode;
  trail?: ReactNode;
  className?: string;
  children?: ReactNode;
};

type AsButton = Common &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color"> & { href?: undefined };

type AsLink = Common & { href: string };

const VARIANT_CLS: Record<Variant, string> = {
  primary:
    "bg-[var(--color-acid)] text-[var(--color-bg)] border-[var(--color-acid-dim)] hover:bg-[#d4ff5f] shadow-[0_0_0_1px_var(--color-acid-dim)_inset,0_8px_20px_-8px_var(--color-acid-glow)]",
  secondary:
    "bg-[var(--color-surface-2)] text-[var(--color-text)] border-[var(--color-border-strong)] hover:border-[var(--color-acid)]",
  ghost:
    "bg-transparent text-[var(--color-text-muted)] border-transparent hover:text-[var(--color-text)]",
  danger:
    "bg-transparent text-[var(--color-danger)] border-[rgba(255,69,58,0.4)] hover:bg-[rgba(255,69,58,0.08)]",
};

const SIZE_CLS: Record<Size, string> = {
  sm: "h-9 px-3 text-[13px]",
  md: "h-12 px-5 text-[15px]",
  lg: "h-14 px-6 text-base",
};

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-[6px] font-body font-semibold border tracking-[0.01em] transition-[transform,background,border-color] duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)] active:translate-y-px disabled:opacity-40 disabled:cursor-not-allowed";

function buildClasses({
  variant = "primary",
  size = "md",
  full,
  className = "",
}: Common): string {
  return `${BASE} ${VARIANT_CLS[variant]} ${SIZE_CLS[size]} ${full ? "w-full" : ""} ${className}`;
}

function Inner({
  icon,
  trail,
  children,
}: {
  icon?: ReactNode;
  trail?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <>
      {icon}
      {children && <span>{children}</span>}
      {trail}
    </>
  );
}

/**
 * Btn — design-canvas button. Use this instead of the existing Button
 * for new design-system pages. Supports primary/secondary/ghost/danger
 * × sm/md/lg, plus leading + trailing icon slots.
 *
 * As a Link with `href` prop, becomes a next/link.
 */
export function Btn(props: AsButton | AsLink) {
  if ("href" in props && props.href !== undefined) {
    const { href, variant, size, full, icon, trail, className, children } = props;
    return (
      <Link
        href={href}
        className={buildClasses({ variant, size, full, className })}
      >
        <Inner icon={icon} trail={trail}>
          {children}
        </Inner>
      </Link>
    );
  }
  const { variant, size, full, icon, trail, className, children, ...rest } = props;
  return (
    <button className={buildClasses({ variant, size, full, className })} {...rest}>
      <Inner icon={icon} trail={trail}>
        {children}
      </Inner>
    </button>
  );
}
